import React, { useState } from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Settings2, Plus, X, MapPin } from "lucide-react";
import { toast } from "sonner";

// A city dropdown backed by the editable per-CRM city list, with a "Manage cities" gear.
export function CitySelect({
  value, onChange, cities = [], addCity, removeCity,
  testId = "city-select", manageTestId = "manage-cities-btn",
  placeholder = "Select a city", triggerClass = "bg-input/50 border-white/10",
}) {
  const [manageOpen, setManageOpen] = useState(false);
  const [newCity, setNewCity] = useState("");
  const [busy, setBusy] = useState(false);

  // Always include the current value so a stored city that's not in the list still shows.
  const options = Array.from(new Set([...(cities || []), value].filter(Boolean)));

  const handleAdd = async () => {
    const name = newCity.trim();
    if (!name) return;
    setBusy(true);
    try { await addCity(name); setNewCity(""); toast.success(`Added ${name}`); }
    catch (e) { toast.error("Couldn't add city", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };
  const handleRemove = async (name) => {
    try { await removeCity(name); toast.success(`Removed ${name}`); }
    catch (e) { toast.error("Couldn't remove city", { description: e?.response?.data?.detail }); }
  };

  return (
    <div className="flex items-center gap-2">
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className={`flex-1 ${triggerClass}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="glass-strong border-white/10 max-h-64">
          {options.length === 0 && <div className="px-2 py-2 text-xs text-muted-foreground">No cities — add one with the gear.</div>}
          {options.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        </SelectContent>
      </Select>
      <button type="button" onClick={() => setManageOpen(true)} data-testid={manageTestId} title="Manage cities"
        className="grid h-10 w-10 shrink-0 place-items-center rounded-md border border-white/12 bg-white/[0.03] text-muted-foreground transition-colors hover:text-white hover:bg-white/[0.07]">
        <Settings2 className="h-4 w-4" />
      </button>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-md glass-strong border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-xl text-white"><MapPin className="h-5 w-5 text-brand-green" /> Manage cities</DialogTitle>
            <DialogDescription className="text-muted-foreground">Add or remove cities for this workspace. Changes show everywhere a city is picked.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div className="flex gap-2">
              <Input value={newCity} onChange={(e) => setNewCity(e.target.value)} data-testid="new-city-input"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }}
                placeholder="Add a city…" className="bg-input/60 border-white/10 focus-visible:ring-brand-green" />
              <Button onClick={handleAdd} disabled={busy || !newCity.trim()} data-testid="add-city-btn" className="bg-brand-green hover:bg-brand-green/90 text-white"><Plus className="h-4 w-4" /> Add</Button>
            </div>
            <div className="max-h-64 space-y-1.5 overflow-y-auto">
              {(cities || []).length === 0 && <p className="text-sm text-muted-foreground">No cities yet.</p>}
              {(cities || []).map((c) => (
                <div key={c} className="flex items-center justify-between rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2" data-testid="city-row">
                  <span className="text-sm text-white">{c}</span>
                  <button onClick={() => handleRemove(c)} data-testid={`remove-city-${c}`} title={`Remove ${c}`}
                    className="grid h-7 w-7 place-items-center rounded-md border border-brand-red/30 bg-brand-red/10 text-brand-red transition-colors hover:bg-brand-red/20"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
